import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useState
} from 'react';
import { useHookMemo } from 'src/hooks/use-hook-memo';

export interface Action {
  id: string;
  title: string;
  icon?: string;
  disabled?: boolean;
  onClick?: () => void;
}

export interface ActionContextType {
  actions: Action[];
  addAction: (action: Action) => void;
  removeAction: (id: string) => void;
}

export const ActionContext = createContext<ActionContextType | undefined>(
  undefined
);

/**
 * Provide a way to define actions in children
 *
 * This is useful for e.g. tab layouts, where action buttons are rendered
 * outside of the tabs, but trigger actions depending on the active tab (like
 * adding a new item).
 */
export const ActionProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const [actions, setActions] = useState<Action[]>([]);

  const removeAction = useCallback((id: string) => {
    setActions((current) => current.filter((action) => action.id !== id));
  }, []);

  const addAction = useCallback((action: Action) => {
    setActions((current) => [...current, action]);
  }, []);

  const context = useHookMemo({
    actions,
    addAction,
    removeAction
  });

  return (
    <ActionContext.Provider value={context}>{children}</ActionContext.Provider>
  );
};
